import React, { useEffect, useMemo, useState } from 'react';
import { CheckCircle2, Eye, Filter, ListOrdered, Search, TimerReset } from 'lucide-react';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { getAiDashboardAddonTotals } from '../lib/aiDashboardAddons';
import { getErrorMessage } from '../lib/utils';
import { workspaceAdminAPI } from '../services/api';
import { useTableControls } from '../hooks/useTableControls';
import { toast } from 'sonner';
import { StatSkeletonGrid, EmptyState } from '../components/common/States';
import { StatCard } from '../components/common/StatCard';

const formatMoney = (value, currency = 'INR') =>
  new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: currency || 'INR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number(value || 0));

const statusBadgeVariant = (status) =>
  status === 'Active' || status === 'Approved'
    ? 'success'
    : status === 'Pending'
      ? 'warning'
      : 'soft';

const planBadgeVariant = (plan) =>
  String(plan || '').toLowerCase() === 'growth' || String(plan || '').toLowerCase() === 'pro'
    ? 'info'
    : 'soft';

const formatOrderDate = (value) => {
  if (!value) return '-';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '-';
  return parsed.toISOString().slice(0, 10);
};

const toTitleCase = (value) => {
  const source = String(value || '').trim();
  if (!source) return 'Unassigned';
  return source
    .split(/[\s_-]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join(' ');
};

const SuperadminPlanOrdersPage = ({ onNavigate }) => {
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [rows, setRows] = useState([]);

  useEffect(() => {
    let mounted = true;

    const loadRows = async () => {
      try {
        setLoading(true);
        const [tenantsResponse, plansResponse] = await Promise.all([
          workspaceAdminAPI.listTenants(),
          workspaceAdminAPI.listSubscriptionPlans({ include_inactive: true }),
        ]);
        if (!mounted) return;

        const tenantRows = Array.isArray(tenantsResponse?.data) ? tenantsResponse.data : [];
        const plansPayload = plansResponse?.data;
        const plansList = Array.isArray(plansPayload)
          ? plansPayload
          : Array.isArray(plansPayload?.items)
            ? plansPayload.items
            : [];
        const planMap = new Map(plansList.map((plan) => [String(plan.id), plan]));

        const mappedRows = tenantRows.map((tenant) => {
          const plan = planMap.get(String(tenant.subscription_plan_id || ''));
          const effectiveUsageLimits = {
            ...(plan?.usage_limits || {}),
            ...(tenant.subscription_usage_limits_override || {}),
            pricing_controls: plan?.pricing_controls || {},
          };
          const addonTotals = getAiDashboardAddonTotals(effectiveUsageLimits);
          const currency = addonTotals.currency || plan?.pricing_controls?.currency || 'INR';
          const originalPrice = Number(plan?.monthly_price || 0);
          const finalPrice = originalPrice + Number(addonTotals.monthly_price || 0);
          const normalizedStatus = toTitleCase(tenant.subscription_status || tenant.status || 'pending');
          const orderDate = tenant.subscription_started_at || tenant.created_at || null;

          return {
            id: tenant.id,
            orderNumber: tenant.slug || `tenant-${tenant.id}`,
            company: tenant.name || `Tenant ${tenant.id}`,
            email: tenant.tenant_admin_email || '-',
            plan: tenant.subscription_plan_name || tenant.subscription_plan_code || 'Unassigned',
            originalPrice,
            discount: null,
            finalPrice,
            currency,
            status: normalizedStatus,
            hasReceipt: false,
            orderDate,
            orderDateLabel: formatOrderDate(orderDate),
          };
        });

        setRows(mappedRows);
      } catch (error) {
        if (!mounted) return;
        setRows([]);
        toast.error(getErrorMessage(error, 'Failed to load plan orders'));
      } finally {
        if (mounted) setLoading(false);
      }
    };

    loadRows();
    return () => {
      mounted = false;
    };
  }, []);

  const filteredRows = useMemo(() => {
    return rows.filter((row) => {
      const normalizedSearch = searchTerm.trim().toLowerCase();
      const matchesSearch =
        !normalizedSearch ||
        `${row.orderNumber} ${row.company} ${row.email} ${row.plan}`.toLowerCase().includes(normalizedSearch);
      const matchesStatus = statusFilter === 'all' || row.status.toLowerCase() === statusFilter;
      return matchesSearch && matchesStatus;
    });
  }, [rows, searchTerm, statusFilter]);

  const table = useTableControls(filteredRows, {
    initialSortKey: 'orderDate',
    initialSortDirection: 'desc',
  });

  const approvedCount = filteredRows.filter((row) => row.status === 'Active' || row.status === 'Approved').length;
  const pendingCount = filteredRows.filter((row) => row.status === 'Pending').length;

  if (loading) {
    return (
      <div className="space-y-6">
        <div>
          <h2 className="text-2xl font-bold tracking-tight">Plan Order Details</h2>
          <p className="text-sm text-muted-foreground">
            Review subscription plan purchases, receipts, and approval status for each organization.
          </p>
        </div>
        <StatSkeletonGrid count={3} className="lg:grid-cols-3" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold tracking-tight">Plan Order Details</h2>
        <p className="text-sm text-muted-foreground">
          Review subscription plan purchases, receipts, and approval status for each organization.
        </p>
      </div>

      <div className="grid gap-4 sm:grid-cols-3">
        <StatCard label="Total Orders" value={filteredRows.length} icon={ListOrdered} tone="primary" />
        <StatCard label="Approved" value={approvedCount} icon={CheckCircle2} tone="success" />
        <StatCard label="Pending" value={pendingCount} icon={TimerReset} tone="warning" />
      </div>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle>Plan Orders</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-col gap-3 xl:flex-row xl:items-center xl:justify-between">
            <div className="flex flex-1 flex-col gap-3 md:flex-row">
              <div className="relative w-full md:max-w-sm">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  value={searchTerm}
                  onChange={(event) => setSearchTerm(event.target.value)}
                  placeholder="Search..."
                  className="pl-9"
                />
              </div>
              <div className="flex items-center gap-2">
                <Button variant="outline" type="button">
                  <Search className="h-4 w-4" />
                  Search
                </Button>
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="w-[150px]">
                    <Filter className="h-4 w-4 text-muted-foreground" />
                    <SelectValue placeholder="Filters" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Statuses</SelectItem>
                      <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="approved">Approved</SelectItem>
                    <SelectItem value="pending">Pending</SelectItem>
                      <SelectItem value="suspended">Suspended</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="flex items-center gap-2 self-end xl:self-auto">
              <span className="text-xs text-muted-foreground">Per Page:</span>
              <Select value={String(table.pageSize)} onValueChange={(value) => table.onPageSizeChange(parseInt(value, 10))}>
                <SelectTrigger className="w-[88px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {table.pageSizes.map((size) => (
                    <SelectItem key={size} value={String(size)}>
                      {size}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="overflow-hidden rounded-xl border">
            <Table>
              <TableHeader className="bg-muted/40">
                <TableRow className="hover:bg-muted/40">
                  <TableHead>#</TableHead>
                  <TableHead>Order Number</TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>Plan</TableHead>
                  <TableHead>Original Price</TableHead>
                  <TableHead>Discount</TableHead>
                  <TableHead>Final Price</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Receipt</TableHead>
                  <TableHead>Order Date</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {table.data.map((row, index) => (
                  <TableRow key={row.id}>
                    <TableCell>{(table.page - 1) * table.pageSize + index + 1}</TableCell>
                    <TableCell className="font-medium">{row.orderNumber}</TableCell>
                    <TableCell>
                      <div className="min-w-[180px]">
                        <div className="font-medium text-foreground">{row.company}</div>
                        <div className="text-xs text-muted-foreground">{row.email}</div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant={planBadgeVariant(row.plan)}>
                        {row.plan}
                      </Badge>
                    </TableCell>
                    <TableCell>{formatMoney(row.originalPrice, row.currency)}</TableCell>
                    <TableCell>{row.discount ? `-${formatMoney(row.discount, row.currency)}` : '-'}</TableCell>
                    <TableCell>{formatMoney(row.finalPrice, row.currency)}</TableCell>
                    <TableCell>
                      <Badge variant={statusBadgeVariant(row.status)}>
                        {row.status}
                      </Badge>
                    </TableCell>
                    <TableCell><span className="text-muted-foreground">-</span></TableCell>
                    <TableCell>{row.orderDateLabel}</TableCell>
                    <TableCell>
                      <div className="flex items-center justify-end gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-primary"
                          onClick={() => onNavigate?.(`tenants/edit/${row.id}`)}
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
                {table.data.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={11} className="p-0">
                      <EmptyState
                        icon={ListOrdered}
                        title="No plan orders found"
                        description="No subscription plan purchases match the current search and filters."
                      />
                    </TableCell>
                  </TableRow>
                ) : null}
              </TableBody>
            </Table>
          </div>

          <div className="flex flex-col gap-3 border-t pt-4 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-sm text-muted-foreground">
              Showing page {table.page} of {table.totalPages} ({table.totalRows} orders)
            </p>
            <div className="flex items-center gap-2">
              <Button variant="outline" onClick={table.goToPreviousPage} disabled={table.page <= 1}>
                Previous
              </Button>
              <Button variant="outline" onClick={table.goToNextPage} disabled={table.page >= table.totalPages}>
                Next
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default SuperadminPlanOrdersPage;
